import { NumberField } from "@/components/ui/number-field";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { BarChart3, Plus, RotateCcw, Settings, X, GripVertical } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { WIDGET_REGISTRY } from "@/lib/widget-registry";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { cn } from "@/lib/utils";

type AnalyticsWidget = {
  id: string;
  tenantId: string;
  userId: string;
  widgetType: string;
  title: string | null;
  size: 'full' | 'half' | 'quarter';
  orderIndex: number;
  column: number;
  isVisible: boolean;
  settings: Record<string, any>;
  page: string;
  createdAt: Date | string;
  updatedAt: Date | string;
};

const ANALYTICS_WIDGET_TYPES = [
  'analytics-goals-overview',
  'analytics-journal-insights',
  'analytics-habit-performance',
  'analytics-calendar-activity',
  'analytics-category-performance',
  'analytics-recent-activity',
  'analytics-productivity-insights',
  'analytics-tasks-overview',
  'analytics-meditation-stats',
];

function AnalyticsWidgetShell({ 
  widgetId, 
  widgetType, 
  title, 
  size, 
  children, 
  onEdit, 
  onDelete, 
  isDragging 
}: {
  widgetId: string;
  widgetType: string;
  title?: string | null;
  size: 'full' | 'half' | 'quarter';
  children: React.ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
  isDragging?: boolean;
}) {
  const config = WIDGET_REGISTRY[widgetType];
  const displayTitle = title || config?.defaultTitle || 'Widget';
  
  return (
    <div 
      className={cn(
        "widget-container h-full",
        isDragging && "opacity-50"
      )}
      data-testid={`analytics-widget-${widgetId}`}
    >
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-lg font-semibold">{displayTitle}</CardTitle>
          <div className="flex items-center gap-2">
            {onEdit && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onEdit} 
                data-testid={`button-edit-analytics-widget-${widgetId}`}
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={onDelete} 
                data-testid={`button-delete-analytics-widget-${widgetId}`}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            <div 
              className="cursor-grab active:cursor-grabbing" 
              data-testid={`drag-handle-analytics-${widgetId}`}
            >
              <GripVertical className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}

function SortableAnalyticsWidget({ 
  widget, 
  onEdit, 
  onDelete 
}: { 
  widget: AnalyticsWidget; 
  onEdit: () => void; 
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  
  const config = WIDGET_REGISTRY[widget.widgetType];
  if (!config) return null;
  
  const WidgetComponent = config.component;
  
  const sizeClasses = 
    widget.size === 'full' ? "col-span-12" :
    widget.size === 'half' ? "col-span-12 md:col-span-6" :
    "col-span-12 md:col-span-6 lg:col-span-3";
  
  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      {...attributes} 
      {...listeners}
      className={sizeClasses}
    >
      <AnalyticsWidgetShell
        widgetId={widget.id}
        widgetType={widget.widgetType}
        title={widget.title}
        size={widget.size}
        onEdit={onEdit}
        onDelete={onDelete}
        isDragging={isDragging}
      >
        <WidgetComponent {...widget.settings} />
      </AnalyticsWidgetShell>
    </div>
  );
}

function AddAnalyticsWidgetDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void; }) {
  const addWidgetMutation = useMutation({
    mutationFn: async (widgetType: string) => {
      const config = WIDGET_REGISTRY[widgetType];
      return apiRequest("POST", "/api/analytics/widgets", {
        widgetType,
        size: config.defaultSize,
        settings: config.defaultSettings,
        page: 'analytics',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/analytics/widgets"] });
      onClose();
    },
  });

  const analyticsWidgets = Object.values(WIDGET_REGISTRY).filter(
    widget => ANALYTICS_WIDGET_TYPES.includes(widget.type)
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Analytics Widget</DialogTitle>
          <DialogDescription>
            Choose a widget to add to your analytics dashboard
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid grid-cols-1 gap-3 max-h-[60vh] overflow-y-auto">
          {analyticsWidgets.map((widget) => {
            const Icon = widget.icon;
            return (
              <div
                key={widget.type}
                className="flex items-start gap-4 p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-foreground mb-1">
                    {widget.name}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {widget.description}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => addWidgetMutation.mutate(widget.type)}
                  disabled={addWidgetMutation.isPending}
                  data-testid={`button-add-analytics-widget-${widget.type}`}
                >
                  Add
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function EditAnalyticsWidgetDialog({ 
  widget, 
  isOpen, 
  onClose 
}: { 
  widget: AnalyticsWidget | null; 
  isOpen: boolean; 
  onClose: () => void; 
}) {
  const [title, setTitle] = useState("");
  const [size, setSize] = useState<'full' | 'half' | 'quarter'>('full');
  const [maxItems, setMaxItems] = useState<number>(5);
  const [showProgress, setShowProgress] = useState(true);
  const [showDetails, setShowDetails] = useState(true);
  const [showStreak, setShowStreak] = useState(true);

  const updateWidgetMutation = useMutation({
    mutationFn: async (updates: Partial<AnalyticsWidget>) => {
      return apiRequest("PATCH", `/api/analytics/widgets/${widget!.id}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/analytics/widgets"] });
      onClose();
    },
  });

  const handleOpen = () => {
    if (widget) {
      setTitle(widget.title || "");
      setSize(widget.size);
      setMaxItems(widget.settings?.maxItems || 5);
      setShowProgress(widget.settings?.showProgress !== false);
      setShowDetails(widget.settings?.showDetails !== false);
      setShowStreak(widget.settings?.showStreak !== false);
    }
  };

  const handleSave = () => {
    if (!widget) return;

    const config = WIDGET_REGISTRY[widget.widgetType];
    const updates: Partial<AnalyticsWidget> = {
      title: title || null,
      size,
      settings: {
        ...widget.settings,
        ...(config?.defaultSettings?.maxItems !== undefined ? { maxItems } : {}),
        ...(config?.defaultSettings?.showProgress !== undefined ? { showProgress } : {}),
        ...(config?.defaultSettings?.showDetails !== undefined ? { showDetails } : {}),
        ...(config?.defaultSettings?.showStreak !== undefined ? { showStreak } : {}),
      }
    };

    updateWidgetMutation.mutate(updates);
  };

  if (!widget) return null;

  const config = WIDGET_REGISTRY[widget.widgetType];
  if (!config) return null;

  const hasMaxItemsSetting = config.defaultSettings?.maxItems !== undefined;
  const hasShowProgressSetting = config.defaultSettings?.showProgress !== undefined;
  const hasShowDetailsSetting = config.defaultSettings?.showDetails !== undefined;
  const hasShowStreakSetting = config.defaultSettings?.showStreak !== undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" onOpenAutoFocus={handleOpen}>
        <DialogHeader>
          <DialogTitle>Edit Widget</DialogTitle>
          <DialogDescription>
            Customize your {config.name} widget
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="widget-title">Widget Title</Label>
            <Input
              id="widget-title"
              placeholder={config.defaultTitle}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="input-analytics-widget-title"
            />
            <p className="text-xs text-muted-foreground">
              Leave empty to use default title
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="widget-size">Widget Size</Label>
            <Select value={size} onValueChange={(value: any) => setSize(value)}>
              <SelectTrigger id="widget-size" data-testid="select-analytics-widget-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {config.allowedSizes.includes('full') && (
                  <SelectItem value="full">Full Width</SelectItem>
                )}
                {config.allowedSizes.includes('half') && (
                  <SelectItem value="half">Half Width</SelectItem>
                )}
                {config.allowedSizes.includes('quarter') && (
                  <SelectItem value="quarter">Quarter Width</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {hasMaxItemsSetting && (
            <div className="space-y-2">
              <Label htmlFor="max-items">Maximum Items</Label>
              <NumberField
                id="max-items"
                min={1}
                max={20}
                emptyValue={5}
                value={maxItems}
                onValueChange={(v) => setMaxItems(v ?? 5)}
                data-testid="input-analytics-max-items"
              />
              <p className="text-xs text-muted-foreground">
                Number of items to display in this widget
              </p>
            </div>
          )}

          {hasShowProgressSetting && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="show-progress"
                checked={showProgress}
                onChange={(e) => setShowProgress(e.target.checked)}
                className="rounded"
                data-testid="checkbox-show-progress"
              />
              <Label htmlFor="show-progress">Show Progress Bar</Label>
            </div>
          )}

          {hasShowDetailsSetting && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="show-details"
                checked={showDetails}
                onChange={(e) => setShowDetails(e.target.checked)}
                className="rounded"
                data-testid="checkbox-show-details"
              />
              <Label htmlFor="show-details">Show Details</Label>
            </div>
          )}

          {hasShowStreakSetting && (
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="show-streak"
                checked={showStreak}
                onChange={(e) => setShowStreak(e.target.checked)}
                className="rounded"
                data-testid="checkbox-show-streak"
              />
              <Label htmlFor="show-streak">Show Streak Badge</Label>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updateWidgetMutation.isPending}
            data-testid="button-save-analytics-widget"
          >
            {updateWidgetMutation.isPending ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AnalyticsPage() {
  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<AnalyticsWidget | null>(null);
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [widgetToDelete, setWidgetToDelete] = useState<string | null>(null);

  const { data: widgets = [], isLoading } = useQuery<AnalyticsWidget[]>({
    queryKey: ["/api/analytics/widgets"],
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const reorderMutation = useMutation({
    mutationFn: async (updatedWidgets: Array<{id: string; orderIndex: number; column: number}>) => {
      return apiRequest("POST", "/api/analytics/widgets/reorder", { widgets: updatedWidgets });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/analytics/widgets"] });
    },
  });

  const deleteWidgetMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("DELETE", `/api/analytics/widgets/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/analytics/widgets"] });
      setWidgetToDelete(null);
    },
  });

  const resetMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/analytics/widgets/reset");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/analytics/widgets"] });
      setIsResetDialogOpen(false);
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = widgets.findIndex(w => w.id === active.id);
    const newIndex = widgets.findIndex(w => w.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(widgets, oldIndex, newIndex);
    const updates = newOrder.map((w, index) => ({
      id: w.id,
      orderIndex: index,
      column: w.column,
    }));

    reorderMutation.mutate(updates);
  };

  const handleConfirmDelete = () => {
    if (widgetToDelete) {
      deleteWidgetMutation.mutate(widgetToDelete);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-screen bg-background items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="px-4 sm:px-6 py-4 sm:py-6 border-b">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold flex items-center gap-2">
                <BarChart3 className="h-6 w-6" />
                Analytics
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">Track your progress and insights</p>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsResetDialogOpen(true)}
                data-testid="button-reset-analytics"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset Layout
              </Button>
              <Button
                size="sm"
                onClick={() => setIsAddWidgetOpen(true)}
                data-testid="button-add-analytics-widget"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Widget
              </Button>
            </div>
          </div>
        </div>

        <div className="flex-1 p-6 overflow-auto">
          {widgets.length === 0 ? (
            <Card className="flex flex-col items-center justify-center py-12">
              <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No analytics widgets yet</h3>
              <p className="text-muted-foreground mb-4">Add widgets to customize your analytics dashboard</p>
              <Button onClick={() => setIsAddWidgetOpen(true)} data-testid="button-add-first-widget">
                <Plus className="h-4 w-4 mr-2" />
                Add Your First Widget
              </Button>
            </Card>
          ) : (
            <DndContext 
              sensors={sensors} 
              collisionDetection={closestCenter} 
              onDragEnd={handleDragEnd}
            >
              <SortableContext 
                items={widgets.map(w => w.id)} 
                strategy={verticalListSortingStrategy}
              >
                <div className="grid grid-cols-4 md:grid-cols-12 gap-4">
                  {widgets.map((widget) => (
                    <SortableAnalyticsWidget
                      key={widget.id}
                      widget={widget}
                      onEdit={() => setEditingWidget(widget)}
                      onDelete={() => setWidgetToDelete(widget.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </div>

      <AddAnalyticsWidgetDialog
        isOpen={isAddWidgetOpen}
        onClose={() => setIsAddWidgetOpen(false)}
      />

      <EditAnalyticsWidgetDialog
        widget={editingWidget}
        isOpen={!!editingWidget}
        onClose={() => setEditingWidget(null)}
      />

      <AlertDialog open={!!widgetToDelete} onOpenChange={() => setWidgetToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Widget</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this widget from your analytics dashboard?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete-widget"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Analytics Dashboard</AlertDialogTitle>
            <AlertDialogDescription>
              This will reset your analytics dashboard to the default layout. All custom widget configurations will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resetMutation.mutate()}
              data-testid="button-confirm-reset"
            >
              Reset to Defaults
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
